import { Card } from '../primitives/Card'
import { Icon } from '../primitives/Icon'
import { formatDays, toNumber } from '../../lib/format'
import {
  formatExitDate,
  isLeapYear,
  parseUtcDate,
  roundHalf,
} from './_accrualUtils'

export interface ProRatedBarProps {
  /** HR-set full annual grant in days. The leaver entitlement is derived
   *  from this via per-day proration. */
  entitlement: number
  /** ISO date `YYYY-MM-DD` of the employee's last working day
   *  (resignation). Applies to any employee type. */
  lastWorkingDay?: string | null
  /** ISO date `YYYY-MM-DD` of the fixed-term contract end. When both
   *  this and `lastWorkingDay` are set, the EARLIER date wins. */
  contractEndDate?: string | null
  /** Optional override for "now"; tests pass a fixed date. */
  now?: Date
}

interface LeavingState {
  /** Leaver entitlement: per-day prorate of full grant to LWD, rounded
   *  by HR's leaver rule (0.01–0.49 → down, 0.50–0.59 → .5, 0.6+ → up).
   *  The full pool the employee can already apply for. */
  leaverDays: number
  /** Full annual grant (denominator in the badge). */
  fullGrant: number
  /** Forfeited days = fullGrant − leaverDays, rounded the same way. */
  forfeitedDays: number
  /** Inclusive day count Jan 1 → LWD. */
  daysToExit: number
  /** Calendar days in display year (365 or 366). */
  daysInYear: number
  /** ISO `YYYY-MM-DD` of the effective exit (earlier of LWD / contract). */
  exitIso: string
}

function compute(props: ProRatedBarProps): LeavingState | null {
  const now = props.now ?? new Date()
  const candidates = [props.lastWorkingDay, props.contractEndDate].filter(
    (d): d is string => typeof d === 'string' && d.length > 0,
  )
  if (candidates.length === 0) return null

  // Lexicographic min on ISO YYYY-MM-DD strings = chronological min.
  const exitIso = candidates.reduce((a, b) => (a < b ? a : b))
  const exit = parseUtcDate(exitIso)
  const entitlement = toNumber(props.entitlement)
  const displayYear = now.getUTCFullYear()
  const exitYear = exit.getUTCFullYear()

  // Only render when the exit is in the current calendar year and hasn't
  // already passed. Past exits → employee is gone; future-year exits →
  // current-year grant is unaffected yet.
  if (exitYear !== displayYear) return null
  if (exit.getTime() < now.getTime()) {
    const exitDay = exit.toISOString().slice(0, 10)
    const nowDay = now.toISOString().slice(0, 10)
    if (exitDay < nowDay) return null
  }

  const yearStart = new Date(Date.UTC(displayYear, 0, 1))
  const daysInYear = isLeapYear(displayYear) ? 366 : 365
  const oneDay = 24 * 60 * 60 * 1000
  // Inclusive day count: Jan 1 → 1 day, Jan 2 → 2 days, … Dec 31 → 365.
  const daysToExit =
    Math.round((exit.getTime() - yearStart.getTime()) / oneDay) + 1
  const leaverDays = roundHalf((entitlement * daysToExit) / daysInYear)
  const forfeitedDays = roundHalf(entitlement - leaverDays)

  return {
    leaverDays,
    fullGrant: entitlement,
    forfeitedDays,
    daysToExit,
    daysInYear,
    exitIso,
  }
}

const YEAR_QUARTER_LABELS: ReadonlyArray<string> = ['Jan', 'Apr', 'Jul', 'Oct', 'Dec']

/**
 * Pro-rated annual leave visualisation for employees leaving the
 * organisation within the current calendar year.
 *
 * Per Policy Library §1.2 "Notice Period / Leaver":
 *
 *     leaver_entitlement = full_grant × (days Jan 1 → LWD inclusive)
 *                                       / days_in_year
 *
 * Frames the year as a single horizontal timeline: the entitled window
 * (Jan 1 → LWD) is filled, the forfeited tail (LWD → Dec 31) is dashed.
 * A marker above the bar points at the last working day. The badge shows
 * the leaver entitlement over the full grant — the pool the employee can
 * already apply for in one go, not a monthly drip.
 *
 * For first-year monthly accrual use `AccrualBar` instead.
 */
export function ProRatedBar(props: ProRatedBarProps): JSX.Element | null {
  const state = compute(props)
  if (state === null) return null

  const entitledPct = (state.daysToExit / state.daysInYear) * 100
  const exitLabel = formatExitDate(state.exitIso)
  const subtitle = `Last day ${exitLabel} — apply all ${formatDays(state.leaverDays)} days before then.`

  return (
    <Card padding={16} style={{ marginTop: 16 }} data-testid="pro-rated-bar">
      <header
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 12,
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            fontSize: 13,
            fontWeight: 700,
            color: 'var(--text)',
          }}
        >
          <Icon name="event_available" size={16} aria-hidden="true" />
          <span>Final year, pro-rated annual leave</span>
        </div>
        <span style={{ fontSize: 11, color: 'var(--text-3)' }}>{subtitle}</span>
      </header>

      <div
        data-testid="pro-rated-timeline"
        data-mode="leaving"
        style={{ position: 'relative', paddingTop: 26, paddingBottom: 18 }}
      >
        {/* Exit-date marker, anchored at the entitled-window edge. */}
        <div
          data-testid="pro-rated-exit-marker"
          style={{
            position: 'absolute',
            top: 0,
            left: `${entitledPct}%`,
            transform: 'translateX(-50%)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
            fontSize: 10,
            fontWeight: 700,
            color: 'var(--steel-blue)',
            whiteSpace: 'nowrap',
          }}
        >
          <span>{exitLabel}</span>
          <span aria-hidden="true" style={{ lineHeight: 1 }}>▼</span>
        </div>

        {/* The bar itself: entitled fill + forfeited tail. */}
        <div
          style={{
            position: 'relative',
            display: 'flex',
            height: 14,
            borderRadius: 7,
            overflow: 'hidden',
            background: 'var(--surface-2)',
          }}
        >
          <div
            data-testid="pro-rated-entitled-fill"
            data-pct={entitledPct.toFixed(2)}
            style={{
              width: `${entitledPct}%`,
              background: 'var(--steel-blue)',
            }}
          />
          <div
            data-testid="pro-rated-forfeited-fill"
            style={{
              width: `${100 - entitledPct}%`,
              backgroundImage:
                'repeating-linear-gradient(45deg, var(--french-gray-2) 0 4px, transparent 4px 8px)',
              borderLeft: '1px dashed var(--border)',
            }}
          />
        </div>

        {/* Month tick labels under the bar. */}
        <div
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            justifyContent: 'space-between',
            fontSize: 9,
            color: 'var(--text-3)',
            fontWeight: 500,
          }}
        >
          {YEAR_QUARTER_LABELS.map((label) => (
            <span key={label}>{label}</span>
          ))}
        </div>
      </div>

      <div
        style={{
          marginTop: 6,
          display: 'flex',
          justifyContent: 'space-between',
          gap: 12,
          fontSize: 11,
          color: 'var(--text-3)',
        }}
      >
        <span data-testid="pro-rated-entitled-label">
          <strong style={{ color: 'var(--text)' }}>
            {formatDays(state.leaverDays)} days
          </strong>{' '}
          entitled
        </span>
        <span data-testid="pro-rated-forfeited-label">
          {formatDays(state.forfeitedDays)} days forfeited
        </span>
      </div>
    </Card>
  )
}
